import { useNavigate, useParams } from "react-router-dom-v5-compat";
import { useSelector } from "react-redux";
import { useCallback, useEffect } from "react";
import { getBookingSupportId } from "@hopper-b2b/api";
import { BookingType } from "@hopper-b2b/types";
import type { ExternalResourceSuccess } from "@overrides/types";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";

/**
 * This hook helps get selected itinerary, methods to jump to customer support and to back to itinerary page,
 * we can reuse the hook for CFAR and disruption protection exercise pages.
 */
export const useFintechExercisePage = (
  itinerarySelector,
  itineraryPagePath: string,
  customerSupportPath: string
) => {
  const navigate = useNavigate();
  const selectedItinerary: ExternalResourceSuccess<BookedFlightItineraryWithDepartureTime> =
    useSelector(itinerarySelector);
  const itinerary = selectedItinerary?.data;
  const params = useParams<{ reservationId: string }>();

  useEffect(() => {
    // To prevent users access Fintech exercise page directly
    if (!itinerary && !params.reservationId) navigate("/");
  }, [itinerary, navigate, params.reservationId]);

  const backToItineraryDetailPage = useCallback(() => {
    navigate(`${itineraryPagePath}/${itinerary?.bookedItinerary?.id}`);
  }, [itinerary, navigate]);

  const contactCustomerSupport = useCallback(async () => {
    const supportId = await getBookingSupportId(
      itinerary?.bookedItinerary?.id,
      BookingType.Flight
    );
    navigate({
      pathname: customerSupportPath,
      search: `supportId=${supportId}`,
    });
  }, [itinerary, navigate]);

  return { itinerary, backToItineraryDetailPage, contactCustomerSupport };
};
